import { HttpClient, HttpErrorResponse, HttpEvent, HttpResponse } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { ConfigService, isErrorHandledByInterceptor, isNetworkError } from '@orion-platform/core';
import { environment } from '@orion/environment';
import { Helper } from 'apps/orion/src/app/helper/helper';
import { NotificationService } from 'libs/core/src/lib/services';
import { Observable, Subject } from 'rxjs';
import { SlotCalendar, SlotFilter, SlotShipmentDTO } from '../models/slot-calendar.model';
import { GlobalStorage } from '../models/slots-storage.model';
import {
  CopyCancelPayload,
  CopyClearenceStatusPayload,
  CopyConfirmationPayload,
  CopyPackagingPayload,
  CreateSlotProps,
  CreateSlotRecurrence,
  NextOrClose,
  SlotActions,
  SlotDetail,
  SlotDetailFieldUpdate,
  SlotDocument,
  SlotDocumentData,
  SlotDocumentInfo,
  SlotHeader,
  SlotMaterial,
  SlotRecurrence,
  SlotShipment,
  SlotSummaryResponse,
  UploadSlotDocumentDto,
} from '../store/slot-calendar/slot-calendar.types';
import {
  buildSlotDocumentFormData,
  prepareSlotsFilter,
  toFieldUpdates,
} from '../store/slot-calendar/slot-calendar.utils';
import { Slot, SlotPackaging, UpdateSlotPayload } from '../store/slots/slots.types';
import { generateHttpParams, getUrl } from '../utils/general.utils';
import { splitWith } from '../utils/string.utils';

@Injectable({
  providedIn: 'root',
})
export class SlotCalendarService {
  private readonly http = inject(HttpClient);
  private readonly config = inject(ConfigService);
  private readonly notifications = inject(NotificationService);

  private readonly mm = environment.modulesBaseUrl.materialManagement;
  private readonly baseUrl = this.config.getSettings('apiBaseUrl') as string;

  private readonly documentData = new Subject<SlotDocumentData>();
  private readonly isNextOrCloseClicked = new Subject<NextOrClose>();
  private readonly slotDocumentDetail = new Subject<SlotDocument>();

  getSlotDocumentDataToOpen(): Observable<SlotDocumentData> {
    return this.documentData.asObservable();
  }

  setSlotDocumentDataToOpen(data: SlotDocumentData): void {
    this.documentData.next(data);
  }

  getNextOrCloseClicked(): Observable<NextOrClose> {
    return this.isNextOrCloseClicked.asObservable();
  }

  setNextOrCloseClicked(isClicked: boolean, currentStep: SlotActions): void {
    this.isNextOrCloseClicked.next({ currentStep, isClicked });
  }

  getSlotDocumentDetail(): Observable<unknown> {
    return this.slotDocumentDetail.asObservable();
  }

  setSlotDocumentDetail(document: SlotDocument): void {
    this.slotDocumentDetail.next(document);
  }

  private slotsUrl(): string {
    return getUrl(this.baseUrl, this.mm.slots);
  }

  private slotStoragesUrl(): string {
    return getUrl(this.baseUrl, this.mm.slotStorages);
  }

  private downloadCalendarUrl(): string {
    return getUrl(this.baseUrl, this.mm.downloadCalendar);
  }

  getSlotById(id: string): Observable<Slot> {
    const url = `${this.slotsUrl()}/${id}`;
    return this.http.get<Slot>(url);
  }

  createSlot(data: CreateSlotProps): Observable<Slot> {
    const url = `${this.slotsUrl()}/Create`;
    return this.http.post<Slot>(url, data);
  }

  moveSlotAndUpdateSlot(data: UpdateSlotPayload): Observable<Slot> {
    const url = `${this.slotsUrl()}/Move`;
    return this.http.post<Slot>(url, data);
  }

  cancelSlot(id: string): Observable<Slot> {
    const url = `${this.slotsUrl()}/${id}/Cancel`;
    return this.http.put<Slot>(url, null);
  }

  downloadXlsxCalendar(filter: SlotFilter): Observable<Blob> {
    const dtoFilter = prepareSlotsFilter(filter);
    const url = this.downloadCalendarUrl();
    return this.http.post(url, dtoFilter, { responseType: 'blob' });
  }

  getSlotsData(filter: SlotFilter): Observable<SlotCalendar> {
    const dtoFilter = prepareSlotsFilter(filter);
    const url = `${this.slotsUrl()}/Calendar`;
    return this.http.post<SlotCalendar>(url, dtoFilter);
  }

  getCalendarData(filter: SlotFilter): Observable<SlotCalendar> {
    const dtoFilter = prepareSlotsFilter(filter);

    const url = `${this.slotsUrl()}/Calendar`;
    return this.http.post<SlotCalendar>(url, dtoFilter);
  }

  getSlotStorages(): Observable<GlobalStorage[]> {
    const url = `${this.slotStoragesUrl()}/GetStorages`;
    return this.http.get<GlobalStorage[]>(url);
  }

  getWorkFlowForCreate(type: string): Observable<string[]> {
    const url = `${this.slotsUrl()}/WorkflowForCreate`;
    const params = generateHttpParams({ type });
    return this.http.get<string[]>(url, { params });
  }

  getWorkFlowForEdit(type: string, id: string): Observable<string[]> {
    const url = `${this.slotsUrl()}/WorkflowForEdit`;
    const params = generateHttpParams({ id, type });
    return this.http.get<string[]>(url, { params });
  }

  getSlotSummary(slotId: string): Observable<SlotSummaryResponse> {
    const url = `${this.slotsUrl()}/${slotId}/Summary`;
    return this.http.get<SlotSummaryResponse>(url);
  }

  getHeader(data: { id: string; type: string }): Observable<SlotHeader> {
    const url = `${this.slotsUrl()}/Header`;
    const params = generateHttpParams({ id: data.id, type: data.type });
    return this.http.get<SlotHeader>(url, { params });
  }

  updateHeader(payload: Partial<SlotHeader>, id: string): Observable<Slot> {
    const url = `${this.slotsUrl()}/Header/${id}`;
    const fields = toFieldUpdates<SlotHeader>(payload);
    return this.http.put<Slot>(url, { fields });
  }

  getSlotShipments(slotId: string): Observable<SlotShipment[]> {
    const url = `${this.slotsUrl()}/${slotId}/Shipments`;
    return this.http.get<SlotShipment[]>(url);
  }

  addSlotShipments(data: SlotShipmentDTO, slotId: string): Observable<SlotShipment[]> {
    const url = `${this.slotsUrl()}/${slotId}/Shipments`;
    const payload = {
      keys: splitWith(
        data.shipmentNumbers ? data.shipmentNumbers.toString() : data.shipmentNumbers,
      ),
    };
    return this.http.post<SlotShipment[]>(url, payload);
  }

  deleteSlotShipment(slotId: string, slotShipmentId: string): Observable<boolean> {
    const url = `${this.slotsUrl()}/${slotId}/Shipments/${encodeURIComponent(slotShipmentId)}`;
    return this.http.delete<boolean>(url);
  }

  getSlotDetails(slotId: string): Observable<SlotDetail> {
    const url = `${this.slotsUrl()}/${slotId}/Detail`;
    return this.http.get<SlotDetail>(url);
  }

  updateSlotDetails(payload: Partial<SlotDetail>, slotId: string): Observable<SlotDetail> {
    const url = `${this.slotsUrl()}/${slotId}/Detail`;
    const fields: SlotDetailFieldUpdate[] = toFieldUpdates<SlotDetail>(payload);
    return this.http.put<SlotDetail>(url, { fields });
  }

  addSlotPackaging(payload: SlotPackaging, slotId: string): Observable<SlotPackaging> {
    const url = `${this.slotsUrl()}/${slotId}/Package`;
    return this.http.post<SlotPackaging>(url, payload);
  }

  deleteSlotPackaging(slotId: string, slotPackagingId: string): Observable<boolean> {
    const url = `${this.slotsUrl()}/${slotId}/Package/${slotPackagingId}`;
    return this.http.delete<boolean>(url);
  }

  getClearanceStatusList(): Observable<string[]> {
    const url = `${this.slotsUrl()}/ClearenceStatus`;
    return this.http.get<string[]>(url);
  }

  copyClearanceStatus(payload: CopyClearenceStatusPayload, slotId: string): Observable<boolean> {
    const url = `${this.slotsUrl()}/${slotId}/Recurrences/CopyClearenceStatus`;
    return this.http.post<boolean>(url, payload);
  }

  copyConfirmation(payload: CopyConfirmationPayload, slotId: string): Observable<boolean> {
    const url = `${this.slotsUrl()}/${slotId}/Recurrences/CopyConfirm`;
    return this.http.post<boolean>(url, payload);
  }

  copyPackaging(payload: CopyPackagingPayload, slotId: string): Observable<boolean> {
    const url = `${this.slotsUrl()}/${slotId}/Recurrences/CopyPackage`;
    return this.http.post<boolean>(url, payload);
  }

  copyCancel(payload: CopyCancelPayload, slotId: string): Observable<boolean> {
    const url = `${this.slotsUrl()}/${slotId}/Recurrences/CopyCancel`;
    return this.http.post<boolean>(url, payload);
  }

  carrierSlotConfirm(slotId: string): Observable<Slot> {
    const url = `${this.slotsUrl()}/${slotId}/Confirm`;
    return this.http.put<Slot>(url, null);
  }

  carrierSlotUnconfirm(slotId: string): Observable<Slot> {
    const url = `${this.slotsUrl()}/${slotId}/UnConfirm`;
    return this.http.put<Slot>(url, null);
  }

  getSlotRecurrences(slotId: string): Observable<SlotRecurrence[]> {
    const url = `${this.slotsUrl()}/${slotId}/Recurrences`;
    return this.http.get<SlotRecurrence[]>(url);
  }

  createSlotRecurrences(payload: CreateSlotRecurrence, slotId: string): Observable<SlotRecurrence> {
    const url = `${this.slotsUrl()}/${slotId}/Recurrences`;
    return this.http.post<SlotRecurrence>(url, payload);
  }

  getSlotMaterials(slotId: string): Observable<SlotMaterial[]> {
    const url = `${this.slotsUrl()}/${slotId}/Materials`;
    return this.http.get<SlotMaterial[]>(url);
  }

  getSlotDocuments(slotId: string): Observable<SlotDocument[]> {
    const url = `${this.slotsUrl()}/${slotId}/Documents`;
    return this.http.get<SlotDocument[]>(url);
  }

  getSlotDocumentInfos(slotId: string, slotShipmentId: string): Observable<SlotDocumentInfo[]> {
    const url = `${this.slotsUrl()}/${slotId}/ListDocumentInfos/${encodeURIComponent(slotShipmentId)}`;
    return this.http.get<SlotDocumentInfo[]>(url);
  }

  uploadSlotDocument(
    slotId: string,
    slotShipmentId: string,
    dto: UploadSlotDocumentDto,
  ): Observable<HttpEvent<{ message: string }>> {
    const url = `${this.slotsUrl()}/${slotId}/Documents/${encodeURIComponent(slotShipmentId)}`;
    const formData = buildSlotDocumentFormData(dto);

    return this.http.post<{ message: string }>(url, formData, {
      observe: 'events',
      reportProgress: true,
    });
  }

  downloadSlotDocument(slotId: string, slotShipmentId: string, docName: string): Observable<Blob> {
    const encodedName = encodeURIComponent(String(docName || ''));
    const url = `${this.slotsUrl()}/${slotId}/Documents/${encodeURIComponent(slotShipmentId)}/${encodedName}`;
    return this.http.get(url, { responseType: 'blob' });
  }

  downloadAllSlotDocuments(slotId: string, slotShipmentId: string): Observable<HttpResponse<Blob>> {
    const url = `${this.slotsUrl()}/${slotId}/Documents/${encodeURIComponent(slotShipmentId)}`;
    return this.http.get(url, { observe: 'response', responseType: 'blob' });
  }

  deleteSlotDocument(
    slotId: string,
    slotShipmentId: string,
    fileName: string,
  ): Observable<boolean> {
    const encodedName = encodeURIComponent(String(fileName || ''));
    const url = `${this.slotsUrl()}/${slotId}/documents/${encodeURIComponent(slotShipmentId)}/${encodedName}`;
    return this.http.delete<boolean>(url);
  }

  markArrival(slotId: string, date: string): Observable<Slot> {
    const url = `${this.slotsUrl()}/${slotId}/Arrival`;
    return this.http.put<Slot>(url, date);
  }

  markUnarrival(slotId: string, date: string): Observable<Slot> {
    const url = `${this.slotsUrl()}/${slotId}/UnArrival`;
    return this.http.put<Slot>(url, date);
  }

  markDeparture(slotId: string, date: string): Observable<Slot> {
    const url = `${this.slotsUrl()}/${slotId}/Departure`;
    return this.http.put<Slot>(url, date);
  }

  markUndeparture(slotId: string, date: string): Observable<Slot> {
    const url = `${this.slotsUrl()}/${slotId}/UnDeparture`;
    return this.http.put<Slot>(url, date);
  }

  markUnloadPermitted(slotId: string, date: string): Observable<Slot> {
    const url = `${this.slotsUrl()}/${slotId}/UnloadPermitted`;
    return this.http.put<Slot>(url, date);
  }

  markUnUnloadPermitted(slotId: string, date: string): Observable<Slot> {
    const url = `${this.slotsUrl()}/${slotId}/UnunloadPermitted`;
    return this.http.put<Slot>(url, date);
  }

  scanArrivals(slotId: string, payload: string): Observable<string[]> {
    const url = `${this.slotsUrl()}/${slotId}/ScanArrivals`;
    return this.http.put<string[]>(url, splitWith(payload));
  }

  errorMessageHandling(error: HttpErrorResponse, msg: string): void {
    if (isNetworkError(error) || isErrorHandledByInterceptor(error)) return;

    if (error?.error instanceof Blob && error.error.type === 'application/json') {
      const reader = new FileReader();
      reader.onload = (): void => {
        const errorText = reader.result as string;
        const parsedError = JSON.parse(errorText);
        Helper.showError(this.notifications, parsedError, msg);
      };
      reader.readAsText(error.error);
      return;
    }

    Helper.showError(this.notifications, error, msg);
  }
}
